import * as React from 'react';
import { helpArticleUrl } from '@auctisync/shared';
import { cn } from '../lib/utils.js';

/**
 * In-product Help primitives.
 *
 * Every feature module across AuctiSync uses these three to meet the
 * "docs-as-Definition-of-Done" discipline:
 *
 *   <HelpTooltip>     — ⓘ icon next to a field with a short explanation
 *   <LearnMoreLink>   — "Learn more →" link to a full Help Center article
 *   <EmptyStateHelp>  — empty-state card that doubles as first-time guidance
 *
 * All three deep-link to help.auctisync.com/a/:slug (or a tenant white-label
 * help domain in Module #35). The helpSlug prop is the only required knob.
 */

/**
 * The URL lives in @auctisync/shared so the native app builds the identical
 * one. It used to be inline here, which is why the app shipped no in-app help
 * at all — the only alternative was hardcoding the domain a second time, and
 * Module #35's white-label help domains will move it.
 */
const helpUrl = helpArticleUrl;

/**
 * Small inline "?" icon a user can hover / tap for a short explanation.
 * Clicking opens the full help article in a new tab.
 */
export interface HelpTooltipProps {
  /** Article slug the user is taken to on click (help.auctisync.com/a/:slug). */
  helpSlug: string;
  /** Short inline explanation (1–2 short sentences). Shown in native title tooltip. */
  children: React.ReactNode;
  className?: string;
  /** Optional aria-label; defaults to "Help". */
  ariaLabel?: string;
}

export function HelpTooltip({
  helpSlug,
  children,
  className,
  ariaLabel = 'Help',
}: HelpTooltipProps): React.ReactElement {
  const text = typeof children === 'string' ? children : undefined;
  return (
    <a
      href={helpUrl(helpSlug)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      title={text}
      className={cn(
        'inline-flex h-4 w-4 items-center justify-center rounded-full border text-xs leading-none font-semibold transition-colors',
        'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--brand-accent)] hover:text-[var(--brand-accent)]',
        className,
      )}
    >
      <span aria-hidden>?</span>
    </a>
  );
}

/**
 * "Learn more →" link inline in body copy. Opens the article in the same tab
 * for docs-focused reading; pass newTab={true} to force a new tab.
 */
export interface LearnMoreLinkProps {
  helpSlug: string;
  children?: React.ReactNode;
  newTab?: boolean;
  className?: string;
}

export function LearnMoreLink({
  helpSlug,
  children = 'Learn more',
  newTab = false,
  className,
}: LearnMoreLinkProps): React.ReactElement {
  return (
    <a
      href={helpUrl(helpSlug)}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium underline-offset-4 transition-colors',
        'text-[var(--brand-accent)] hover:underline',
        className,
      )}
    >
      {children}
      <span aria-hidden>→</span>
    </a>
  );
}

/**
 * Empty-state card that doubles as first-time user guidance.
 *
 * Use it as the placeholder when a table has zero rows, a feature hasn't
 * been configured, or a tenant hasn't started onboarding a module. Shows a
 * title, a one-line description, an optional primary CTA, and a Learn More
 * link to the deep docs.
 */
export interface EmptyStateHelpProps {
  title: string;
  description: string;
  helpSlug: string;
  primaryActionLabel?: string;
  primaryActionHref?: string;
  onPrimaryAction?: () => void;
  className?: string;
}

export function EmptyStateHelp({
  title,
  description,
  helpSlug,
  primaryActionLabel,
  primaryActionHref,
  onPrimaryAction,
  className,
}: EmptyStateHelpProps): React.ReactElement {
  const hasCta = Boolean(primaryActionLabel && (primaryActionHref || onPrimaryAction));
  return (
    <div
      className={cn(
        'rounded-lg border p-8 text-center',
        'border-[var(--border)] bg-[var(--surface-muted)]',
        className,
      )}
    >
      <h3 className="text-lg font-semibold text-[var(--text)]">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-[var(--text-muted)]">{description}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {hasCta ? (
          primaryActionHref ? (
            <a
              href={primaryActionHref}
              className="inline-flex items-center justify-center rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] shadow-sm transition-colors hover:bg-[var(--brand-primary-hover,var(--brand-primary))]"
            >
              {primaryActionLabel}
            </a>
          ) : (
            <button
              type="button"
              onClick={onPrimaryAction}
              className="inline-flex items-center justify-center rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] shadow-sm transition-colors hover:bg-[var(--brand-primary-hover,var(--brand-primary))]"
            >
              {primaryActionLabel}
            </button>
          )
        ) : null}
        <LearnMoreLink helpSlug={helpSlug} newTab />
      </div>
    </div>
  );
}
